import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Clock3,
  Download,
  FileUp,
  FolderOpen,
  History,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ACCEPTED_FILES = ".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.zip";
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const EMPTY_UPLOAD = {
  file: null,
  subject: "",
  message: "",
  sensitivity: "Internal",
  recipientIds: [],
  notifyAll: false,
};
const EMPTY_FILES = [];

const when = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : "—";

const size = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const directionLabel = (file) =>
  file.direction === "to_employer" ? "From Employees First" : "From your employer";

const expiryLabel = (file) => {
  if (!file.expires_at) return "No automatic expiry";
  const remaining = new Date(file.expires_at).getTime() - Date.now();
  if (remaining < 7 * 24 * 60 * 60 * 1000) return `Expires soon · ${when(file.expires_at)}`;
  return `Expires ${when(file.expires_at)}`;
};

export default function FilesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [employers, setEmployers] = useState([]);
  const [company, setCompany] = useState("");
  const [workspace, setWorkspace] = useState(null);
  const [audit, setAudit] = useState([]);
  const [upload, setUpload] = useState(EMPTY_UPLOAD);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [removeFile, setRemoveFile] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [policy, setPolicy] = useState(null);
  const [retentionDays, setRetentionDays] = useState("90");
  const [autoExpire, setAutoExpire] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [busy, setBusy] = useState("");

  const activeCompany = isAdmin ? company : user?.company || "";

  const loadWorkspace = useCallback(async () => {
    if (!activeCompany) {
      setWorkspace(null);
      return;
    }
    const { data } = await api.get("/files", { params: isAdmin ? { company: activeCompany } : {} });
    setWorkspace(data);
  }, [activeCompany, isAdmin]);

  const loadAdminData = useCallback(async () => {
    if (!isAdmin) return;
    const [employerResponse, policyResponse] = await Promise.all([
      api.get("/admin/files/employers"),
      api.get("/admin/files/policy"),
    ]);
    setEmployers(employerResponse.data.employers);
    setPolicy(policyResponse.data);
    setRetentionDays(String(policyResponse.data.days));
    setAutoExpire(Boolean(policyResponse.data.auto_expire));
  }, [isAdmin]);

  const loadAudit = useCallback(async () => {
    if (!isAdmin || !activeCompany) {
      setAudit([]);
      return;
    }
    const { data } = await api.get("/admin/files/audit", { params: { company: activeCompany } });
    setAudit(data.events);
  }, [activeCompany, isAdmin]);

  useEffect(() => {
    loadAdminData().catch((error) => toast.error(formatApiError(error.response?.data?.detail)));
  }, [loadAdminData]);

  useEffect(() => {
    loadWorkspace().catch((error) => toast.error(formatApiError(error.response?.data?.detail)));
    loadAudit().catch((error) => toast.error(formatApiError(error.response?.data?.detail)));
  }, [loadAudit, loadWorkspace]);

  useEffect(() => {
    if (!isAdmin || !activeCompany) {
      setRecipients([]);
      return;
    }
    api.get("/admin/files/recipients", { params: { company: activeCompany } })
      .then(({ data }) => setRecipients(data.recipients))
      .catch(() => setRecipients([]));
  }, [activeCompany, isAdmin]);

  const files = workspace?.files || EMPTY_FILES;
  const sent = useMemo(
    () => files.filter((file) => file.direction === "to_employer").length,
    [files],
  );

  const receive = useMemo(
    () => files.filter((file) => file.direction === "from_employer").length,
    [files],
  );

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!upload.file) {
      toast.error("Choose a file to share");
      return;
    }
    setBusy("upload");
    try {
      const body = new FormData();
      body.append("file", upload.file);
      body.append("subject", upload.subject);
      body.append("message", upload.message);
      if (isAdmin) body.append("sensitivity", upload.sensitivity);
      if (isAdmin) body.append("company", activeCompany);
      if (isAdmin) {
        body.append("notify_all", String(upload.notifyAll));
        upload.recipientIds.forEach((recipientId) => body.append("recipient_ids", recipientId));
      }
      await api.post("/files/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File shared with the employer workspace");
      setUpload(EMPTY_UPLOAD);
      setUploadOpen(false);
      await Promise.all([loadWorkspace(), loadAudit()]);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const download = async (file) => {
    setBusy(`download-${file.id}`);
    try {
      await downloadFile(`/files/${file.id}/download`, file.original_filename);
      await loadAudit();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!removeFile) return;
    setBusy("remove");
    try {
      await api.delete(`/files/${removeFile.id}`, { data: { reason: removeReason } });
      toast.success("File removed from shared access");
      setRemoveFile(null);
      setRemoveReason("");
      await Promise.all([loadWorkspace(), loadAudit()]);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const savePolicy = async () => {
    setBusy("policy");
    try {
      const { data } = await api.put("/admin/files/policy", {
        auto_expire: autoExpire,
        days: Number(retentionDays),
      });
      setPolicy(data);
      toast.success("Default file retention updated for future uploads");
      await loadAudit();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const canUpload = !isAdmin || Boolean(activeCompany);

  return (
    <div
      className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10"
      data-testid="files-page"
    >
      <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="eyebrow mb-2">
            {isAdmin ? "Operations · Employer exchange" : "Shared employer workspace"}
          </div>
          <h1
            className="font-heading text-4xl font-black leading-none tracking-tight md:text-5xl"
            style={{ color: "var(--ink)" }}
            data-testid="files-page-heading"
          >
            Files
          </h1>
          <p
            className="mt-3 max-w-2xl text-sm"
            style={{ color: "var(--muted)" }}
            data-testid="files-page-description"
          >
            {isAdmin
              ? "Select an employer to review both directions of the shared document exchange."
              : "Send documents to Employees First and access files shared with your employer."}
          </p>
        </div>
        {isAdmin && (
          <div className="w-full md:w-80">
            <label className="field-label" htmlFor="file-employer-select">
              Employer workspace
            </label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger
                id="file-employer-select"
                className="!rounded-none"
                data-testid="file-employer-select"
              >
                <SelectValue placeholder="Choose an employer" />
              </SelectTrigger>
              <SelectContent className="!rounded-none max-h-72">
                {employers.map((name) => (
                  <SelectItem key={name} value={name} className="!rounded-none">
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isAdmin && policy && (
        <section
          className="mt-10 border p-6"
          style={{ borderColor: "var(--border)" }}
          data-testid="file-retention-panel"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="eyebrow mb-2 flex items-center gap-2">
                <ShieldCheck size={13} /> Default retention
              </div>
              <p className="max-w-2xl text-sm" style={{ color: "var(--muted)" }}>
                This policy applies only to files uploaded after it is saved. Existing file expiry
                dates do not change.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={savePolicy}
              disabled={busy === "policy"}
              data-testid="file-retention-save"
            >
              {busy === "policy" ? "Saving…" : "Save retention policy"}
            </button>
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-[auto_minmax(160px,220px)] sm:items-end">
            <label
              className="flex items-center gap-3 text-sm font-medium"
              style={{ color: "var(--ink)" }}
            >
              <input
                type="checkbox"
                checked={autoExpire}
                onChange={(event) => setAutoExpire(event.target.checked)}
                data-testid="file-retention-auto-expire"
              />
              Automatically expire new files
            </label>
            <div>
              <label className="field-label" htmlFor="file-retention-days">
                Days available
              </label>
              <input
                id="file-retention-days"
                className="input-flat w-full"
                type="number"
                min="1"
                max="3650"
                disabled={!autoExpire}
                value={retentionDays}
                onChange={(event) => setRetentionDays(event.target.value)}
                data-testid="file-retention-days"
              />
            </div>
          </div>
        </section>
      )}

      {!isAdmin || activeCompany ? (
        <>
          <section
            className="mt-10 grid border grid-borders grid-cols-1 md:grid-cols-3"
            style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            data-testid="files-summary"
          >
            <Summary label="Active files" value={files.length} testId="files-total-count" />
            <Summary label="From Employees First" value={sent} testId="files-sent-count" />
            <Summary label="From your employer" value={receive} testId="files-received-count" />
          </section>

          <section className="mt-10" data-testid="files-workspace">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="eyebrow mb-2 flex items-center gap-2">
                  <FolderOpen size={13} /> {workspace?.company || activeCompany}
                </div>
                <h2 className="font-heading text-2xl font-bold" style={{ color: "var(--ink)" }}>
                  Shared inbox
                </h2>
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setUploadOpen(true)}
                disabled={!canUpload}
                data-testid="files-upload-open"
              >
                <Upload size={15} /> Share a file
              </button>
            </div>
            <div
              className="mt-5 max-h-[560px] overflow-auto border"
              style={{ borderColor: "var(--border)" }}
              data-testid="files-list-region"
            >
              <div
                className={
                  "hidden min-w-[880px] "
                  + "grid-cols-[minmax(250px,1.6fr)_minmax(150px,0.8fr)_150px_170px_100px] "
                  + "border-b px-5 py-3 text-left eyebrow md:grid"
                }
                style={{ borderColor: "var(--border)" }}
              >
                <span>File</span>
                <span>Direction</span>
                <span>Sent</span>
                <span>Expiry</span>
                <span className="text-right">Action</span>
              </div>
              {files.map((file) => (
                <article
                  key={file.id}
                  className="border-b px-5 py-5"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`employer-file-row-${file.id}`}
                >
                  <div
                    className={
                      "grid gap-4 "
                      + "md:grid-cols-[minmax(250px,1.6fr)_minmax(150px,0.8fr)_150px_170px_100px] "
                      + "md:items-center"
                    }
                  >
                    <div className="min-w-0">
                      <div
                        className="font-medium truncate"
                        style={{ color: "var(--ink)" }}
                        data-testid={`employer-file-name-${file.id}`}
                      >
                        {file.original_filename}
                      </div>
                      <div className="mt-1 text-xs" style={{ color: "var(--muted)" }}>
                        {file.subject} · {file.sensitivity} · {size(file.size)} · sent by {file.sender_name}
                      </div>
                      {file.message && (
                        <p
                          className="mt-2 text-xs leading-relaxed"
                          style={{ color: "var(--muted)" }}
                          data-testid={`employer-file-message-${file.id}`}
                        >
                          {file.message}
                        </p>
                      )}
                    </div>
                    <div
                      className="text-sm font-medium"
                      style={{ color: "var(--ink)" }}
                      data-testid={`employer-file-direction-${file.id}`}
                    >
                      {directionLabel(file)}
                    </div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--muted)" }}
                      data-testid={`employer-file-sent-${file.id}`}
                    >
                      {when(file.uploaded_at)}
                    </div>
                    <div
                      className="flex items-center gap-2 text-xs"
                      style={{
                        color: file.expires_at
                          && new Date(file.expires_at).getTime() - Date.now() < 604800000
                          ? "#B45309"
                          : "var(--muted)",
                      }}
                      data-testid={`employer-file-expiry-${file.id}`}
                    >
                      <Clock3 size={13} /> {expiryLabel(file)}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="btn-ghost !p-2"
                        onClick={() => download(file)}
                        disabled={busy === `download-${file.id}`}
                        aria-label={`Download ${file.original_filename}`}
                        data-testid={`employer-file-download-${file.id}`}
                      >
                        <Download size={15} />
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn-ghost !p-2"
                          onClick={() => {
                            setRemoveFile(file);
                            setRemoveReason("");
                          }}
                          aria-label={`Remove ${file.original_filename}`}
                          data-testid={`employer-file-remove-${file.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
              {!files.length && (
                <div
                  className="p-12 text-center text-sm"
                  style={{ color: "var(--muted)" }}
                  data-testid="files-empty"
                >
                  No active files in this workspace.
                </div>
              )}
            </div>
          </section>

          {isAdmin && (
            <section className="mt-12" data-testid="files-audit-section">
              <div className="eyebrow mb-2 flex items-center gap-2">
                <History size={13} /> Exchange audit
              </div>
              <h2 className="font-heading text-2xl font-bold" style={{ color: "var(--ink)" }}>
                Employer file history
              </h2>
              <div
                className="mt-5 max-h-[360px] overflow-auto border"
                style={{ borderColor: "var(--border)" }}
                data-testid="files-audit-region"
              >
                {audit.map((event) => (
                  <div
                    key={event.id}
                    className="grid gap-2 border-b px-5 py-4 text-sm md:grid-cols-[160px_130px_1fr]"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`file-audit-event-${event.id}`}
                  >
                    <span style={{ color: "var(--muted)" }}>{when(event.timestamp)}</span>
                    <span className="font-medium" style={{ color: "var(--ink)" }}>
                      {event.actor}
                    </span>
                    <span style={{ color: "var(--muted)" }}>{event.reason}</span>
                  </div>
                ))}
                {!audit.length && (
                  <div
                    className="p-10 text-center text-sm"
                    style={{ color: "var(--muted)" }}
                    data-testid="files-audit-empty"
                  >
                    No file activity for this employer yet.
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      ) : (
        <div
          className="mt-16 border p-12 text-center"
          style={{ borderColor: "var(--border)" }}
          data-testid="files-select-employer-empty"
        >
          <FolderOpen size={24} className="mx-auto mb-4" style={{ color: "var(--muted)" }} />
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            Choose an employer to open its shared file workspace.
          </p>
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent
          className="!rounded-none max-h-[90vh] overflow-y-auto"
          data-testid="files-upload-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Share a file</DialogTitle>
            <DialogDescription>
              {isAdmin
                ? `This file will be shared with ${activeCompany}.`
                : "Employees First administrators and your employer team can access this file."}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={submitUpload}>
            <div>
              <label className="field-label" htmlFor="files-upload-subject">
                Subject
              </label>
              <input
                id="files-upload-subject"
                className="input-flat w-full"
                value={upload.subject}
                onChange={(event) => setUpload({ ...upload, subject: event.target.value })}
                maxLength={160}
                required
                data-testid="files-upload-subject"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="files-upload-message">
                Message <span className="normal-case">(optional)</span>
              </label>
              <textarea
                id="files-upload-message"
                className="input-flat w-full"
                rows={4}
                value={upload.message}
                onChange={(event) => setUpload({ ...upload, message: event.target.value })}
                maxLength={1200}
                data-testid="files-upload-message"
              />
            </div>
            {isAdmin && (
              <>
              <div>
                <label className="field-label" htmlFor="files-upload-sensitivity">
                  Sensitivity
                </label>
                <select
                  id="files-upload-sensitivity"
                  className="input-flat w-full"
                  value={upload.sensitivity}
                  onChange={(event) => setUpload({ ...upload, sensitivity: event.target.value })}
                  data-testid="files-upload-sensitivity"
                >
                  <option>Internal</option>
                  <option>Confidential</option>
                  <option>Restricted</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="files-upload-recipients">
                  Delivery notifications
                </label>
                <label className="mb-3 flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }}>
                  <input
                    type="checkbox"
                    checked={upload.notifyAll}
                    onChange={(event) => setUpload({ ...upload, notifyAll: event.target.checked })}
                    data-testid="files-upload-notify-all"
                  />
                  Notify all active employer users
                </label>
                <select
                  id="files-upload-recipients"
                  multiple
                  className="input-flat w-full min-h-28"
                  disabled={upload.notifyAll}
                  value={upload.recipientIds}
                  onChange={(event) => setUpload({
                    ...upload,
                    recipientIds: Array.from(event.target.selectedOptions, (option) => option.value),
                  })}
                  data-testid="files-upload-recipients"
                >
                  {recipients.map((recipient) => (
                    <option key={recipient.id} value={recipient.id}>
                      {recipient.name || recipient.email}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                  This sends a notice only. Every employer user can still access the shared file.
                </p>
              </div>
              </>
            )}
            <div>
              <label className="field-label" htmlFor="files-upload-file">
                Document
              </label>
              <input
                id="files-upload-file"
                type="file"
                className="block w-full text-sm"
                accept={ACCEPTED_FILES}
                onChange={(event) => {
                  const selected = event.target.files?.[0] || null;
                  if (selected && selected.size > MAX_FILE_BYTES) {
                    toast.error("Files must be 25 MB or smaller");
                    event.target.value = "";
                    setUpload({ ...upload, file: null });
                    return;
                  }
                  setUpload({ ...upload, file: selected });
                }}
                required
                data-testid="files-upload-file"
              />
              <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                PDF, TXT, CSV, DOC, DOCX, XLS, XLSX, JPG, PNG, WEBP, or ZIP · up to 25 MB
              </p>
            </div>
            <DialogFooter>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setUploadOpen(false)}
                data-testid="files-upload-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={busy === "upload"}
                data-testid="files-upload-submit"
              >
                <FileUp size={15} /> {busy === "upload" ? "Sharing…" : "Share file"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(removeFile)} onOpenChange={(open) => !open && setRemoveFile(null)}>
        <DialogContent className="!rounded-none" data-testid="files-remove-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Remove shared file?</DialogTitle>
            <DialogDescription>
              This immediately removes {removeFile?.original_filename} from every employer workspace
              and stops future downloads. The storage record and audit history remain protected.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="field-label" htmlFor="files-remove-reason">Removal reason</label>
            <textarea
              id="files-remove-reason"
              className="input-flat w-full"
              rows={3}
              value={removeReason}
              onChange={(event) => setRemoveReason(event.target.value)}
              maxLength={300}
              data-testid="files-remove-reason"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setRemoveFile(null)}
              data-testid="files-remove-cancel"
            >
              Keep file
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={remove}
              disabled={removeReason.trim().length < 3 || busy === "remove"}
              data-testid="files-remove-confirm"
            >
              <Trash2 size={15} /> {busy === "remove" ? "Removing…" : "Remove access"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Summary({ label, value, testId }) {
  return (
    <div className="grid-cell p-7" data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className="stat-num mt-3 text-4xl">{value}</div>
    </div>
  );
}